import moment from "moment";
import FamilyMember from "../dtos/FamilyMember";
import InputSelectOption from "../common/components/forms/InputSelectOption";
import UserCompletionState from "../common/types/UserCompletionState";

export const ALL_GENDERS = ['F', 'M', 'O', 'N']
export const ALL_GENDER_RESTRICTIONS = ['woman', 'man', 'other', 'not_answered']

export const getDisplayableAge = (t: (x: string) => string, age: number) => {
    const years = `${Math.floor(age / 12)} ${t('years')}`;
    const months = (age % 12) !== 0 ? ((age % 12) === 6 ? t('andAHalf') : `${age % 12} ${t('months')}`) : '';
    return `${years} ${months}`;
};

export const getDisplayableAgeRestriction = (t: (x: string) => string, min?: number, max?: number) => {
    if (!min && !max) return t('all');
    else if (!min && !!max) {
        return `${getDisplayableAge(t, max)} ${t('andLess')}`;
    }
    else if (!!min && !max) {
        return `${getDisplayableAge(t, min)} ${t('andMore')}`;
    }
    else return `${getDisplayableAge(t, min || 0)} - ${getDisplayableAge(t, max || 0)}`;
};

export const getDisplayableSessionTime = (t: (x: string, options: Record<string, string | number>) => string, isAllDay: boolean, startDate: string, endDate: string, isShortFormat: boolean): string => {
    const startDateMoment = moment(startDate);
    const endDateMoment = moment(endDate);
    const durationFormat = isShortFormat ? 'lll' : 'LLL'
    if (isAllDay) return t('allDayOf$day', {day: startDateMoment.format('LL')});
    else if (startDateMoment.isSame(endDateMoment, 'day')) return t('$dayFrom$time1To$time2', {day: startDateMoment.format('LL'), time1: startDateMoment.format('LT'), time2: endDateMoment.format('LT')});
    else return t('from$startDateTo$endDate', {startDate: startDateMoment.format(durationFormat), endDate: endDateMoment.format(durationFormat)});
};

export const getDisplayableNumberOfPlacesRemaining = (t: (str: string) => string, numberOfPlacesRemaining: number): string => {
    if (numberOfPlacesRemaining >= 5) return '5+';
    if (numberOfPlacesRemaining <= 0) return t('complete');
    return numberOfPlacesRemaining.toString();
};

export const getDisplayableSexRestriction = (translation: (str: string) => string, genders: string[]): string => {
    let gendersText = '';

    if (genders.sort().every((value, index) => value === ALL_GENDERS.sort()[index])) {
        return translation('genderAll');
    }

    for (let [key, gender] of Object.entries(genders)) {
        switch (gender) {
            case 'F': {
                gendersText += translation('genderWomen');
                break;
            }
            case 'M': {
                gendersText += translation('genderMan');
                break;
            }
            case 'O': {
                gendersText += translation('genderOthers');
                break;
            }
            case 'N': {
                gendersText += translation('genderWouldRatherNotAnswer');
                break;
            }
        }

        if (Number(key) !== genders.length - 1) {
            gendersText += ', ';
            continue
        }

        if (Number(key) === genders.length - 2) {
            gendersText += 'and ';
        }
    }

    return gendersText;
};


export const restrictions = {
    groupIsFull: "GROUP_IS_FULL",
    membershipTemplateFamilyOwnerAgeNotSet: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_AGE_NOT_SET",
    membershipTemplateFamilyOwnerAgeTooYoung: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_AGE_TOO_YOUNG",
    membershipTemplateFamilyOwnerAgeTooOld: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_AGE_TOO_OLD",
    membershipTemplateFamilyOwnerGenderNeedsFemale: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_GENDER_NEEDS_FEMALE",
    membershipTemplateFamilyOwnerGenderNeedsMale: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_GENDER_NEEDS_MALE",
    membershipTemplateFamilyOwnerGenderNotSet: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_GENDER_NOT_SET",
    membershipTemplateFamilyOwnerGenderNotRightGender: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_NOT_RIGHT_GENDER_CODE",
    membershipTemplateFamilyOwnerNotRightGender: "MEMBERSHIP_TEMPLATE_FAMILY_OWNER_NOT_RIGHT_GENDER",
    membershipTemplateSubscriberAgeNotSet: "MEMBERSHIP_TEMPLATE_SUBSCRIBER_AGE_NOT_SET",
    membershipTemplateSubscriberAgeTooYoung: "MEMBERSHIP_TEMPLATE_SUBSCRIBER_AGE_TOO_YOUNG",
    membershipTemplateSubscriberAgeTooOld: "MEMBERSHIP_TEMPLATE_SUBSCRIBER_AGE_TOO_OLD",
    membershipTemplateSubscriberGenderNeedsFemale: "MEMBERSHIP_TEMPLATE_SUBSCRIBER_GENDER_NEEDS_FEMALE",
    membershipTemplateSubscriberGenderNeedsMale: "MEMBERSHIP_TEMPLATE_SUBSCRIBER_GENDER_NEEDS_MALE",
    membershipTemplateSubscriberGenderNotSet: "MEMBERSHIP_TEMPLATE_SUBSCRIBER_GENDER_NOT_SET",
    membershipTemplateSubscriberGenderNotRightGender: "MEMBERSHIP_TEMPLATE_SUBSCRIBER_NOT_RIGHT_GENDER_CODE",
    needsFemale: "SUBSCRIBER_GENDER_NEEDS_FEMALE",
    needsMale: "SUBSCRIBER_GENDER_NEEDS_MALE",
    notRightGender: "SUBSCRIBER_DOES_NOT_HAVE_RIGHT_GENDER",
    noSessionIsAvailable: "NO_SESSION_IS_AVAILABLE",
    tooYoung: "SUBSCRIBER_AGE_TOO_YOUNG",
    tooOld: "SUBSCRIBER_AGE_TOO_OLD",
    maxRegistrations: 'SUBSCRIBER_EXCEEDS_MAX_REGISTRATIONS',
    periodRestrictsCity: "PERIOD_RESTRICTS_CITY",
    periodRestrictedByGroup: "PERIOD_RESTRICTED_BY_GROUP",
    planNotActive: "PLAN_NOT_ACTIVE_ANYMORE",
    planNotYetActive: "PLAN_NOT_YET_ACTIVE",
    organizationNeedsPhoneNumber: "ORGANIZATION_NEEDS_PHONE",
    organizationNeedsAddress: "ORGANIZATION_NEEDS_ADDRESS",
    periodRestrictedByMembership: "PERIOD_RESTRICTED_BY_MEMBERSHIP",
    planRestrictedByMembership: "PLAN_RESTRICTED_BY_MEMBERSHIP"
};

export const familyMembersToSelectOptions = (t: (x: string, params: any) => string, familyMembers: {[key: string]: Partial<FamilyMember>}): InputSelectOption<number>[] => {
    return Object.entries(familyMembers).map(([key, member]) => {
        const age = Math.abs(moment(member.birthday).diff(moment(), 'years'));
        return {
            value: Number(member.id),
            label: `${member.full_name} (${t('$yearsYearsOld', {years: age})})`
        }
    });
};

export const hasCompletedRegistration = (completionState: UserCompletionState): boolean => {
    return completionState.birthday && completionState.address && completionState.phoneNumber;
};

interface INbrPlacesRemaining {
    nbr_places_remaining: number | null | undefined
}

export function hasGroupPlacesLimited<T extends INbrPlacesRemaining>(group: T) {
    return typeof group.nbr_places_remaining === 'number'
}

export function hasGroupCompleted<T extends INbrPlacesRemaining>(group: T) {
    return hasGroupPlacesLimited(group) && Number(group.nbr_places_remaining) <= 0
}

export const isNotEmpty = <T>(value: T | null | undefined): value is T => {
    return value !== null && value !== undefined;
}
